import { type ComponentProps, useMemo } from "react";

import { COLORS_VALUES } from "@shared/constants";
import { Button } from "@shared/ui/button";
import { Image } from "@shared/ui/image";
import { Heading } from "@shared/ui/typography";
import {
  formatDate,
  formatTimeToHM,
  isDifferentDate,
} from "@shared/utils/string";

import type { EventType } from "@/_pages/event/model";

import s from "./event-card.module.scss";

export type CompProps = ComponentProps<"div"> & {
  event: EventType;
};

export const EventCard = (props: CompProps) => {
  const { event } = props;

  const style: React.CSSProperties & { ["--hover-color"]: string } = {
    "--hover-color":
      COLORS_VALUES[event.mainColor as keyof typeof COLORS_VALUES],
  };

  const isDifferentYear = useMemo(() => {
    if (!event.date || !event.dateEnd) return false;
    return isDifferentDate(event.date, event.dateEnd, "year");
  }, [event.date, event.dateEnd]);

  const isDifferentMonth = useMemo(() => {
    if (!event.dateEnd) return true;
    return isDifferentDate(event.date, event.dateEnd, "month");
  }, [event.date, event.dateEnd]);

  return (
    <article className={s.root} style={style}>
      <Button className={s.wrapper} href={`/events/${event.slug}`}>
        <div className={s.content}>
          <div className={s.top}>
            <Heading level={5} tag="p">
              {event.date && (
                <span>
                  {formatDate(event.date, isDifferentMonth, isDifferentYear)}
                </span>
              )}
              {event.dateEnd && (
                <span>
                  {" "}
                  – {formatDate(event.dateEnd, true, isDifferentYear)}
                </span>
              )}
              {!event.dateEnd && event.time && (
                <span>, {formatTimeToHM(event.time)}</span>
              )}
            </Heading>
            {event.location && (
              <Heading level={6} tag="p">
                {event.location}
              </Heading>
            )}
          </div>
          <div className={s.bottom}>
            <Heading level={5} tag="h3">
              {event.title}
            </Heading>
            {/* {event.price && (
              <Heading level={6} tag="p" className={s.price}>
                {formatNumberWithSpaces(event.price)}
              </Heading>
            )} */}
          </div>
        </div>
        {event?.image?.url && (
          <Image
            src={event.image.url}
            alt={event.image?.alternativeText ?? "event photo"}
            className={s.image}
            height="100%"
          />
        )}
      </Button>
    </article>
  );
};

EventCard.displayName = "EventCard";
